import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";
import {
  useChatbotActions,
  useChatbotMessages,
} from "stores/default-stores/chatbot-stores";
import { useToggle } from "hooks/useToggle";
import ChatbotTextInput from "./ChatbotTextInput";
import ChatbotHeader from "./ChatbotHeader";
import ChatbotTerminate from "./ChatbotTerminate";
import ChatbotMessages from "./ChatbotMessages";
import ChatbotReset from "./ChatbotReset";
import ChatbotEndConversation from "./ChatbotEndConversation";

const ChatbotContainer = styled("div")({
  height: "100%",
  backgroundColor: "#FAFAFA",
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  // border: "1px solid rgba(0, 0, 0, 0.1)",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
});

export default function Chatbot({ toggleMinimized, onTerminate }) {
  const { t } = useTranslation();
  const { answer, reset, terminate, undoLast, fetchState } =
    useChatbotActions();
  const { isFinal, messages } = useChatbotMessages();

  const {
    isOpen: shouldShowCloseConfirmation,
    open: showCloseConfirmation,
    close: hideCloseConfirmation,
  } = useToggle();
  const {
    isOpen: shouldShowResetConfirmation,
    open: showResetConfirmation,
    close: hideResetConfirmation,
  } = useToggle();

  const isChatContentVisible =
    !shouldShowCloseConfirmation && !shouldShowResetConfirmation;

  const handleReset = useCallback(async () => {
    await reset();
    hideResetConfirmation();
  }, [reset, hideResetConfirmation]);

  const handleTerminate = useCallback(() => {
    terminate();
    onTerminate();
  }, [terminate, onTerminate]);

  useEffect(() => {
    if (typeof answer === "function") {
      answer();
    }
  }, [answer]);

  return (
    <ChatbotContainer className="inner-container">
      <ChatbotHeader
        onMinimize={toggleMinimized}
        onClose={showCloseConfirmation}
      />
      {shouldShowCloseConfirmation && (
        <ChatbotTerminate
          onConfirm={handleTerminate}
          onReject={hideCloseConfirmation}
        />
      )}
      {shouldShowResetConfirmation && (
        <ChatbotReset
          onConfirm={handleReset}
          onReject={hideResetConfirmation}
        />
      )}
      <ChatbotMessages
        messages={messages}
        isLoading={fetchState.isLoading}
        isVisible={isChatContentVisible}
        onSelect={answer}
      />
      {isFinal ? (
        <ChatbotEndConversation
          isVisible={isChatContentVisible}
          onUndo={undoLast}
          onReset={showResetConfirmation}
        />
      ) : (
        <ChatbotTextInput
          isVisible={isChatContentVisible}
          placeholder={t("Chatbot.Filtering")}
          onUndo={undoLast}
          onReset={showResetConfirmation}
        />
      )}
    </ChatbotContainer>
  );
}
